'use client'
import { useCallback, useMemo } from 'react'

// Constants
import { withProductContextProvider } from '@src/quince-site-common/src/contexts/product-context'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductCard from '@src/quince-site-common/src/domains/ProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import PageBuilder from '@src/quince-site-common/src/organisms/pageBuilder'
import BundlePageContext from '@src/quince-site-pdp/src/domains/BundlePageContext'

import ProductPageContextProvider from '../../contexts/ProductPageContextProvider'
import { TPDPSectionData } from '../../types/pdp-section-data.type'
import { ProductSectionType } from '../../types/productPage.types'

import { CUSTOM_SECTION_TYPES_VS_RENDERER, SECTION_TYPES_VS_RENDERER } from './bundlePage.constants'

const useContainerRenderer = (
  bundle: Bundle,
  sectionData: TPDPSectionData,
  recommendedContentfulProductIds: string[] | undefined,
  recommendedProductCards?: ProductCard[],
  mergeProducts?: Product[],
) => {
  return useCallback(
    (props: { sectionType: ProductSectionType; customSectionType: CUSTOM_SECTION_TYPE }) => {
      const sectionType: ProductSectionType = props.sectionType
      const Section = SECTION_TYPES_VS_RENDERER[sectionType]
      if (!Section) {
        if (sectionType === ProductSectionType.CUSTOM_SECTION) {
          const customSectionType: CUSTOM_SECTION_TYPE = props.customSectionType
          const CustomSection = CUSTOM_SECTION_TYPES_VS_RENDERER[customSectionType]
          if (!CustomSection) {
            return <></>
          }
          return (
            <CustomSection
              bundle={bundle}
              recommendedContentfulProductIds={recommendedContentfulProductIds}
              recommendedProductCards={recommendedProductCards}
              mergeProducts={mergeProducts}
              sectionData={sectionData}
            />
          )
        }
        return <></>
      }
      return (
        <Section
          bundle={bundle}
          recommendedContentfulProductIds={recommendedContentfulProductIds}
          recommendedProductCards={recommendedProductCards}
          mergeProducts={mergeProducts}
          sectionData={sectionData}
        />
      )
    },
    [bundle, mergeProducts, recommendedContentfulProductIds, recommendedProductCards],
  )
}

function BundlePage({ pageData }: { pageData: BundlePageContext }) {
  if (!pageData) {
    throw new Error('Page data is not defined please check build script')
  }

  const containerRenderer = useContainerRenderer(
    pageData.bundle,
    pageData.sectionData,
    pageData.recommendedContentfulProductIds,
    pageData.recommendedProductCards,
    pageData.mergeProducts,
  )
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  const updatePageDataContext: BundlePageContext = useMemo(
    () => ({
      bundle: pageData.bundle,
      sectionData: pageData.sectionData,
      mobileContainerConfig: pageData.mobileContainerConfig,
      desktopContainerConfig: pageData.desktopContainerConfig,
      recommendedContentfulProductIds: pageData.recommendedContentfulProductIds,
    }),
    [pageData],
  )

  return (
    <ProductPageContextProvider>
      <PageBuilder context={updatePageDataContext} selfContainerRenderer={containerRenderer} isMobile={isMobile} />
    </ProductPageContextProvider>
  )
}

export default withProductContextProvider(BundlePage)
